import { getIdToken } from './authService';
import {
  AnalyticsUnavailableError,
  AnalyticsForbiddenError,
  AnalyticsDisabledError,
} from './adminApiService';

/**
 * Calls the Agent-Provider marketplace review API (a separate API Gateway from
 * the Admin API). These routes authenticate the ADMIN Cognito ID token
 * in-handler and gate on the `reviewEnabled` flag in the admin allowlist DB.
 * Configure REACT_APP_PROVIDER_API_URL per environment (test2 only today).
 */
const BASE = process.env.REACT_APP_PROVIDER_API_URL;
// Long-running preview POSTs ride the chat Lambda Function URL (2-min budget)
// when configured — HTTP API hard-caps at 30s, too short for multi-tool turns.
const CHAT_BASE = (process.env.REACT_APP_PROVIDER_CHAT_URL || '').replace(/\/+$/, '') || BASE;

export interface ReviewBot {
  botId: string;
  displayName: string;
  providerId: string;
  status: string;
  botType: string;
  disclaimerCategory: string;
  submittedAt?: string;
  createdAt?: string;
  updatedAt?: string;
  /** Marketing entry point the bot was created from (absent on older bots). */
  entryChannel?: string;
  /** Live bot with a pending instruction edit awaiting quick re-review. */
  editReview?: boolean;
  /** Public expert-page slug (round-3 item 5: per-bot page link). */
  slug?: string;
  /** false = the provider still needs identity verification (Legal-5). */
  providerVerified?: boolean;
  /** Provider labels (メッセージアナリティクス provider picker). */
  providerEmail?: string;
  providerName?: string;
}

export interface KnowledgeFile {
  fileId: string;
  fileName: string;
  /** Provider's short name/tag for the file (e.g. 「料金表 2026年版」). */
  label?: string;
  /** Provider's own explanation of the file contents (optimizer hint). */
  description?: string;
  ingestStatus?: string;
  uploadedAt?: string;
  moderation?: string;
}

export interface BotConfig {
  ragType?: string;
  tools?: string[];
  llm?: string;
  model?: string;
}

export interface LifecycleEvent {
  eventTs: string;
  from?: string;
  to?: string;
  by?: string;
  note?: string;
  reasons?: string[];
}

export interface BotDetail {
  bot: ReviewBot & { description?: string };
  instructions: string;
  persona?: string;
  /** Item 1: pending edit of a LIVE bot awaiting quick re-review. */
  pendingInstructions?: string | null;
  pendingPersona?: string | null;
  /** Web-search opt-in (2026-08-23): live flag, and the pending one when an edit changes it. */
  webSearch?: boolean;
  pendingWebSearch?: boolean | null;
  editReview?: boolean;
  config?: BotConfig | null;
  profile: { slug: string; title: string; bio: string } | null;
  /** Voice devices (plan/VOICE_DEVICE_CLIENT_PLAN.md): switches + today's usage. */
  voice?: VoiceSummary;
  provider: {
    providerId: string; email?: string; name?: string; verificationStatus: string;
    /** Account-level access switch (false = suspended by an admin). */
    accessEnabled?: boolean;
    suspendedAt?: string; suspendNote?: string;
  };
}

/** Item 1: approve (promote to live) or reject (discard) a pending edit. */
export async function adminEditReview(botId: string, approve: boolean, note?: string, reasons?: string[]): Promise<void> {
  const r = await req<{ error?: string }>(`/provider/admin/bots/${encodeURIComponent(botId)}/edit-review`, 'POST', { approve, note, reasons });
  if (r.status >= 300) throw new Error(r.body.error || `HTTP ${r.status}`);
}

async function req<T>(path: string, method: string, body?: unknown, base: string | undefined = BASE): Promise<{ status: number; body: T }> {
  const idToken = await getIdToken();
  if (!idToken) throw new Error('Not authenticated');
  // Function URLs (CHAT_BASE) reserve `Authorization` for IAM — bearer tokens
  // ride a custom header there (server copies it back).
  const authHeader = base === BASE ? 'Authorization' : 'x-tokuru-authorization';
  const res = await fetch(`${base}${path}`, {
    method,
    headers: { 'Content-Type': 'application/json', [authHeader]: `Bearer ${idToken}` },
    body: body ? JSON.stringify(body) : undefined,
  });
  const json = (await res.json().catch(() => ({}))) as T;
  return { status: res.status, body: json };
}

/** Whether this admin may review bots (drives the Review tab's visibility). */
/**
 * Who the API thinks is signed in, and what they may do.
 *
 * `/provider/admin/permissions` has always returned the email alongside
 * `canReview`; the console discarded it. Showing it matters operationally:
 * review access is granted per email in the admin allowlist, so an admin
 * looking at an empty review queue needs to see WHICH account the API is
 * answering for (2026-08-30).
 */
export interface AdminIdentity { email: string; canReview: boolean }

export async function getAdminIdentity(): Promise<AdminIdentity> {
  if (!BASE) return { email: '', canReview: false };
  try {
    const r = await req<{ email?: string; canReview?: boolean }>('/provider/admin/permissions', 'GET');
    if (r.status !== 200) return { email: '', canReview: false };
    return { email: r.body.email || '', canReview: !!r.body.canReview };
  } catch {
    return { email: '', canReview: false };
  }
}

export async function getReviewPermission(): Promise<boolean> {
  return (await getAdminIdentity()).canReview;
}

export async function getReviewQueue(): Promise<ReviewBot[]> {
  const r = await req<{ queue: ReviewBot[] }>('/provider/admin/review-queue', 'GET');
  if (r.status !== 200) throw new Error(`(${r.status}) ${JSON.stringify(r.body)}`);
  return r.body.queue || [];
}

export async function advanceBot(botId: string, to: string, note?: string, reasons?: string[]): Promise<void> {
  const body: Record<string, unknown> = { to };
  if (note) body.note = note;
  if (reasons && reasons.length) body.reasons = reasons; // required by the server for rejections
  const r = await req(`/provider/admin/bots/${encodeURIComponent(botId)}/lifecycle`, 'POST', body);
  if (r.status >= 300) throw new Error(`(${r.status}) ${JSON.stringify(r.body)}`);
}

/**
 * Rename any bot, including the platform-managed (`configSource: 'file'`) ones
 * the provider console refuses. Updates Bots.displayName AND the public
 * ExpertProfiles.title together — until 2026-08-11 nothing could update either
 * after creation.
 */
export async function renameBot(botId: string, displayName: string): Promise<void> {
  const r = await req(`/provider/admin/bots/${encodeURIComponent(botId)}/profile`, 'POST', { displayName });
  if (r.status >= 300) throw new Error(`(${r.status}) ${JSON.stringify(r.body)}`);
}

/**
 * Voice devices (plan/VOICE_DEVICE_CLIENT_PLAN.md). A bot is reachable from a
 * paired voice device only when all three switches are on: the env kill
 * switch, this platform switch (the platform pays for STT/TTS in the PoC), and
 * the provider's own toggle.
 */
export interface VoiceSummary {
  envEnabled: boolean;
  platformEnabled: boolean;
  providerEnabled: boolean;
  activeDevices: number;
  today: { utterances: number; audioSeconds: number; ttsChars: number; estUsd: number };
}

export async function setBotVoice(botId: string, enabled: boolean): Promise<VoiceSummary> {
  const r = await req<{ voice: VoiceSummary }>(`/provider/admin/bots/${encodeURIComponent(botId)}/voice`, 'POST', { enabled });
  if (r.status >= 300) throw new Error(`(${r.status}) ${JSON.stringify(r.body)}`);
  return r.body.voice;
}

/** Status audit trail for one bot (who moved it, when, and any note). */
export async function getBotLifecycle(botId: string): Promise<LifecycleEvent[]> {
  const r = await req<{ events: LifecycleEvent[] }>(`/provider/admin/bots/${encodeURIComponent(botId)}/lifecycle`, 'GET');
  if (r.status !== 200) throw new Error(`(${r.status}) ${JSON.stringify(r.body)}`);
  return r.body.events || [];
}

export async function verifyProvider(providerId: string): Promise<void> {
  const r = await req(`/provider/admin/providers/${encodeURIComponent(providerId)}/verify`, 'POST');
  if (r.status >= 300) throw new Error(`(${r.status}) ${JSON.stringify(r.body)}`);
}

/**
 * Provider ACCOUNT suspension (2026-07-09): flips AgentProviders.accessEnabled
 * (blocks every provider-console/API route) and emails the provider. Distinct
 * from per-bot Suspend, which unpublishes one bot.
 */
export async function suspendProvider(providerId: string, note?: string): Promise<{ emailSent: boolean }> {
  const r = await req<{ emailSent: boolean; error?: string }>(
    `/provider/admin/providers/${encodeURIComponent(providerId)}/suspend`, 'POST', { note });
  if (r.status >= 300) throw new Error(`(${r.status}) ${JSON.stringify(r.body)}`);
  return { emailSent: !!r.body.emailSent };
}

export async function reinstateProvider(providerId: string): Promise<{ emailSent: boolean }> {
  const r = await req<{ emailSent: boolean; error?: string }>(
    `/provider/admin/providers/${encodeURIComponent(providerId)}/reinstate`, 'POST', {});
  if (r.status >= 300) throw new Error(`(${r.status}) ${JSON.stringify(r.body)}`);
  return { emailSent: !!r.body.emailSent };
}

// ── Support threads (provider ↔ admin, 2026-07-09) ──────────────────────────

export interface SupportThread {
  threadId: string;
  subject: string;
  status: 'open' | 'answered' | 'closed';
  createdAt?: string;
  updatedAt?: string;
  lastSender?: 'provider' | 'admin';
  messageCount?: number;
  providerId?: string;
  providerEmail?: string;
  providerName?: string;
  /** Bot the provider picked when opening the thread (2026-09-12; optional). */
  botId?: string;
  botName?: string;
  /** Fallback context: display names of every bot the provider runs. */
  providerBotNames?: string[];
}

export interface SupportMessage {
  sender: 'provider' | 'admin';
  body: string;
  at: string;
  by?: string;
}

export async function getSupportThreads(status?: string): Promise<SupportThread[]> {
  const qs = status && status !== 'all' ? `?status=${encodeURIComponent(status)}` : '';
  const r = await req<{ threads: SupportThread[] }>(`/provider/admin/support/threads${qs}`, 'GET');
  if (r.status !== 200) throw new Error(`(${r.status}) ${JSON.stringify(r.body)}`);
  return r.body.threads || [];
}

export async function getSupportThread(threadId: string): Promise<{ thread: SupportThread; messages: SupportMessage[] }> {
  const r = await req<{ thread: SupportThread; messages: SupportMessage[] }>(
    `/provider/admin/support/threads/${encodeURIComponent(threadId)}`, 'GET');
  if (r.status !== 200) throw new Error(`(${r.status}) ${JSON.stringify(r.body)}`);
  return r.body;
}

export async function replySupportThread(threadId: string, body: string): Promise<{ emailSent: boolean }> {
  const r = await req<{ emailSent: boolean }>(
    `/provider/admin/support/threads/${encodeURIComponent(threadId)}/messages`, 'POST', { body });
  if (r.status >= 300) throw new Error(`(${r.status}) ${JSON.stringify(r.body)}`);
  return { emailSent: !!r.body.emailSent };
}

export async function closeSupportThread(threadId: string): Promise<void> {
  const r = await req(`/provider/admin/support/threads/${encodeURIComponent(threadId)}/close`, 'POST', {});
  if (r.status >= 300) throw new Error(`(${r.status}) ${JSON.stringify(r.body)}`);
}

/** All-bots list (item 4). status='all' (or omitted) returns every bot. */
export async function getAllBots(status?: string): Promise<ReviewBot[]> {
  const qs = status && status !== 'all' ? `?status=${encodeURIComponent(status)}` : '';
  const r = await req<{ bots: ReviewBot[] }>(`/provider/admin/bots${qs}`, 'GET');
  if (r.status !== 200) throw new Error(`(${r.status}) ${JSON.stringify(r.body)}`);
  return r.body.bots || [];
}

/** Full review detail for one bot (item 3): instructions, profile, provider info. */
export async function getBotDetail(botId: string): Promise<BotDetail> {
  const r = await req<BotDetail>(`/provider/admin/bots/${encodeURIComponent(botId)}`, 'GET');
  if (r.status !== 200) throw new Error(`(${r.status}) ${JSON.stringify(r.body)}`);
  return r.body;
}

export async function getBotKnowledge(botId: string): Promise<KnowledgeFile[]> {
  const r = await req<{ files: KnowledgeFile[] }>(`/provider/admin/bots/${encodeURIComponent(botId)}/knowledge`, 'GET');
  if (r.status !== 200) throw new Error(`(${r.status}) ${JSON.stringify(r.body)}`);
  return r.body.files || [];
}

/**
 * Marketing dashboard: raw daily counters (impressions/funnel) over a range.
 * metric key shapes are documented in provider-api/marketing-metrics.ts.
 */
export interface MarketingRow {
  day: string;    // YYYY-MM-DD (UTC)
  metric: string; // e.g. total#lp_view, page#lp_view#expert#ja, entry#signup#self
  count: number;
}

/** Token-usage overview for a month (billable grains only; biller's model). */
export interface UsageBotRow {
  botId: string; providerId: string; displayName?: string;
  promptTokens: number; completionTokens: number; totalTokens: number; messages: number; rawUsd: number;
}
export interface ProviderBillingInfo {
  plan: 'standard';
  /** Admin waive switch — the month is recorded but not charged. */
  trialActive: boolean;
  /** Inside the platform free trial (date-driven, distinct from the waive). */
  inFreeTrial?: boolean;
  trialEndsAt?: string | null;
  feeFactor?: number;
  feeMinorPerBot?: number; flatCurrency?: 'usd' | 'jpy'; includedUsdPerBot?: number;
  estAmountMinor?: number; flatMinor?: number; overageMinor?: number;
}
export interface UsageProviderRow {
  providerId: string; email?: string | null; totalTokens: number; messages: number; rawUsd: number; estChargeUsd: number;
  publishedBots?: number;
  billing?: ProviderBillingInfo;
  /** Usage-cap Phase 2: current-cycle raw spend vs the applicable cap. */
  cap?: { cycleStart: string; cycleEnd: string; spendUsd: number; capUsd: number; proven: boolean; usageCapUsd: number | null } | null;
  /** The stored override itself — present even when no cycle exists yet. */
  usageCapUsd?: number | null;
  /** Secret FIELD the provider's NEW bots run OpenAI on (null = platform key). */
  openaiKeyField?: string | null;
  invoice: { status: string; amountMinor: number | null; currency: string | null; carryOutUsd: number; stripeInvoiceId: string | null } | null;
}
export interface AdminUsage { month: string; bots: UsageBotRow[]; providers: UsageProviderRow[] }

/**
 * Per-provider OpenAI key (2026-07-27): stores the secret FIELD name (the key
 * itself lives only in Secrets Manager; ops adds it there first). Applies to
 * bots created after it is set; null reverts new bots to the platform key.
 */
export async function setProviderOpenAIKeyField(providerId: string, keyField: string | null): Promise<void> {
  const r = await req(`/provider/admin/providers/${encodeURIComponent(providerId)}/openai-key`, 'POST', { keyField });
  if (r.status >= 300) throw new Error(`(${r.status}) ${JSON.stringify(r.body)}`);
}

/**
 * Waive (or un-waive) a provider's charges. One plan for everyone since
 * 2026-08-10 — the endpoint no longer accepts a plan, fee, or credit, and
 * rejects a body that names one.
 */
export async function setProviderUsageCap(providerId: string, usageCapUsd: number | null): Promise<void> {
  const r = await req(`/provider/admin/providers/${encodeURIComponent(providerId)}/usage-cap`, 'POST', { usageCapUsd });
  if (r.status >= 300) throw new Error(`(${r.status}) ${JSON.stringify(r.body)}`);
}

export async function setProviderBillingWaive(providerId: string, trialActive: boolean): Promise<void> {
  const r = await req(`/provider/admin/providers/${encodeURIComponent(providerId)}/billing-plan`, 'POST', { trialActive });
  if (r.status >= 300) throw new Error(`(${r.status}) ${JSON.stringify(r.body)}`);
}
export async function getAdminUsage(month?: string): Promise<AdminUsage> {
  const qs = month ? `?month=${encodeURIComponent(month)}` : '';
  const r = await req<AdminUsage>(`/provider/admin/usage${qs}`, 'GET');
  if (r.status !== 200) throw new Error(`(${r.status}) ${JSON.stringify(r.body)}`);
  return r.body;
}

export async function getMarketingMetrics(from: string, to: string): Promise<MarketingRow[]> {
  const r = await req<{ rows: MarketingRow[] }>(
    `/provider/admin/marketing?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`, 'GET',
  );
  if (r.status !== 200) throw new Error(`(${r.status}) ${JSON.stringify(r.body)}`);
  return r.body.rows || [];
}

/** Admin preview: send a test message to a bot before approving it. */
export async function adminTestBot(botId: string, message: string): Promise<string> {
  const r = await req<{ answer?: string; error?: string }>(
    `/provider/admin/bots/${encodeURIComponent(botId)}/test`, 'POST', { message }, CHAT_BASE,
  );
  if (r.status !== 200) throw new Error(`(${r.status}) ${JSON.stringify(r.body)}`);
  return r.body.answer || '';
}

// ── Optimizer (design §9) ────────────────────────────────────────────────────

export interface OptimizerDecision { step: string; decision: string; rationale: string; at: string }
export interface OptimizerEvalResult {
  question: string; source: 'knowledge' | 'readjust_note'; answer: string;
  grade: 'good' | 'partial' | 'poor' | 'error'; judgeNotes: string;
}
export interface OptimizerRunSummary {
  botId?: string; runTs: string; status: string; trigger: string;
  displayName?: string; providerId?: string; inputNote?: string;
  decisions: number; evalSummary?: { good: number; partial: number; poor: number; errors: number };
  metrics?: Record<string, number>; error?: string;
  createdAt?: string; updatedAt?: string; finishedAt?: string;
}
export interface OptimizerRefineIteration {
  iteration: number; addendum: string; improved: boolean;
  good: number; partial: number; poor: number; errors: number;
}
export interface OptimizerRunDetail {
  botId: string; runTs: string; status: string; trigger: string;
  inputNote?: string; requestedBy?: string;
  decisions: OptimizerDecision[];
  inventory: Array<{ fileId: string; fileName: string; description?: string; size?: number; s3Key?: string }>;
  artifacts: Array<{ kind: string; s3Key: string; entries: number }>;
  provisionedTools: Array<{ toolName: string; aliasName: string; documents: number }>;
  configDiff?: { before: Record<string, unknown>; after: Record<string, unknown> };
  evalResults: OptimizerEvalResult[];
  evalSummary?: { good: number; partial: number; poor: number; errors: number };
  refineIterations?: OptimizerRefineIteration[];
  optimizerAddendum?: string;
  metrics?: Record<string, number>; error?: string;
  cancelledBy?: string; approvedBy?: string;
  createdAt?: string; startedAt?: string; finishedAt?: string;
}
export interface OptimizerArtifactSamples {
  kind: string; entries: number; samples: Array<Record<string, unknown>>;
}

/** The two optimizer queue tabs: 最適化中 (queued/running) and 承認待ち. */
export async function getOptimizerQueue(): Promise<{ optimizing: OptimizerRunSummary[]; awaitingReview: OptimizerRunSummary[] }> {
  const r = await req<{ optimizing: OptimizerRunSummary[]; awaitingReview: OptimizerRunSummary[] }>('/provider/admin/optimizer/queue', 'GET');
  if (r.status !== 200) throw new Error(`(${r.status}) ${JSON.stringify(r.body)}`);
  return { optimizing: r.body.optimizing || [], awaitingReview: r.body.awaitingReview || [] };
}

export async function getOptimizerRuns(botId: string): Promise<OptimizerRunSummary[]> {
  const r = await req<{ runs: OptimizerRunSummary[] }>(`/provider/admin/bots/${encodeURIComponent(botId)}/optimizer/runs`, 'GET');
  if (r.status !== 200) throw new Error(`(${r.status}) ${JSON.stringify(r.body)}`);
  return r.body.runs || [];
}

export async function getOptimizerRunDetail(botId: string, runTs: string): Promise<OptimizerRunDetail> {
  const r = await req<{ run: OptimizerRunDetail }>(`/provider/admin/bots/${encodeURIComponent(botId)}/optimizer/runs/${encodeURIComponent(runTs)}`, 'GET');
  if (r.status !== 200) throw new Error(`(${r.status}) ${JSON.stringify(r.body)}`);
  return r.body.run;
}

/** Samples of extracted entries so the reviewer can judge extraction quality. */
export async function getOptimizerArtifacts(botId: string, runTs: string): Promise<OptimizerArtifactSamples[]> {
  const r = await req<{ artifacts: OptimizerArtifactSamples[] }>(
    `/provider/admin/bots/${encodeURIComponent(botId)}/optimizer/runs/${encodeURIComponent(runTs)}/artifacts`, 'GET');
  if (r.status !== 200) throw new Error(`(${r.status}) ${JSON.stringify(r.body)}`);
  return r.body.artifacts || [];
}

// ── Provider-bot message analytics ─────────────────────────────────────────
// The provider console's 分析 view for ANY provider's bot: same actions, same
// SQL, same shapes the owner sees (no broadcasts — those are LINE-plane only).

export type ProviderAnalyticsAction = 'overview' | 'messages-daily' | 'top-users' | 'user-messages';

export interface ProviderBotOverview {
  total_messages: number;
  active_users: number;
  user_messages: number;
  assistant_messages: number;
  provider_messages: number;
}

export interface ProviderBotUserMessage {
  message_timestamp: string;
  role: 'user' | 'assistant' | 'provider' | 'system';
  content: string;
  source: string;
}

export async function getProviderBotAnalytics<T>(
  botId: string,
  action: ProviderAnalyticsAction,
  params: { from?: string; to?: string; userId?: string },
): Promise<T> {
  const qs = new URLSearchParams();
  if (params.from) qs.set('from', params.from);
  if (params.to) qs.set('to', params.to);
  if (params.userId) qs.set('userId', params.userId);
  const r = await req<{ data?: T; error?: string; code?: string }>(
    `/provider/admin/bots/${encodeURIComponent(botId)}/analytics/${action}?${qs.toString()}`, 'GET',
  );
  if (r.status === 503) throw new AnalyticsUnavailableError();
  if (r.status === 403) {
    if (r.body.code === 'analytics_disabled') throw new AnalyticsDisabledError();
    throw new AnalyticsForbiddenError();
  }
  if (r.status >= 300) throw new Error(r.body.error || `HTTP ${r.status}`);
  return r.body.data as T;
}
